import React, { useEffect, useState, useCallback } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  createStyles,
  makeStyles,
} from "@material-ui/core";
import _ from "lodash";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import ValidationMessage from "../../../../../../components/validation/ValidationMessage";
import DialogCommonDefault from "../../../../../../components/dialogs/DialogCommonDefault";
import TextfieldDefaultNew from "../../../../../../components/textField/TextfieldDefaultNew";
import { Autocomplete } from "@material-ui/lab";
import {
  ERROR_MESSAGE_UNEXPECTED_ERROR,
  SUCCESSFULLY_CREATED,
} from "../../../../../../utils/consts";
import { updateDirectoryInStore } from "../../../../../../redux/actions/menu";
import {
  createProductWrapper,
  updateWrapperInfo,
} from "../../../../../../services/menuApp/menuService";
// ✅ ADDED: API call to fetch Item Types (same as OverviewSection.tsx)
import { fetchItemTypesDetails } from "../../../../../../services/menuApp/categoryMenuService";
import { useRouteMatch } from "react-router-dom";
import ButtonCommon from "../../../../../../components/buttons/ButtonCommon";
import { CustomTheme } from "../../../../../../types/customTheme";
import ColorSelection from "../../../../../PrestoExpressEatprestoApp/pages/ThemeCustomizationPage/ColorSelection";

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    autoComplete: {
      marginTop: "4px",
      "& .MuiAutocomplete-input": {
        color: theme.palette.custom.orange.contrastText,
      },
      "& .MuiInputBase-root": {
        color: "inherit",
        backgroundColor: theme.palette.background.entity_background,
        borderRadius: 10,
      },
      [`& fieldset`]: {
        borderRadius: 10,
        border: "none",
        color: theme.palette.custom.orange.contrastText,
      },
    },
    titleColor: {
      color: theme.palette.custom.orange.contrastText,
    },
    labelStyle: {
      fontSize: "14px",
      [theme.breakpoints.down("xs")]: {
        fontSize: "12px",
      },
    },
  }),
);

export interface ProductAddModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  handleCloseProductAddModal: () => void;
  stockDepartmentSelectedNode: any[];
  categoryId: any;
  directoryId: any;
  directoryLevel: number;
  menuNodeList: any;
  selectedMenuId: string;
  firstWrapperId: any;
  setMenuNodeList: (list: any) => void;
  setSuccess: (message: string) => void;
  setError: (message: string) => void;
  isRoot: boolean;
  menuId: string;
  setIsOpenDirectoryOrProductSelectionModal: (open: boolean) => void;
  onItemAdded?: (newItem?: any) => void;
  wrapperId?: any;
  availableTags?: any;
  selectedProduct?: any[]; // ✅ ADDED: optional prop for existing products
}

const ProductAddModal: React.FunctionComponent<ProductAddModalProps> = ({
  isOpen,
  setIsOpen,
  handleCloseProductAddModal,
  stockDepartmentSelectedNode,
  categoryId,
  directoryId,
  directoryLevel,
  menuNodeList,
  selectedMenuId,
  firstWrapperId,
  setMenuNodeList,
  setSuccess,
  setError,
  isRoot,
  menuId,
  setIsOpenDirectoryOrProductSelectionModal,
  onItemAdded,
  wrapperId,
  availableTags,
  selectedProduct, // ✅ ADDED: receive selectedProduct prop
}) => {
  const match: any = useRouteMatch();
  const dispatch = useDispatch();
  const [selectedDepartmentObj, setSelectedDepartmentObj] = useState<any>({});
  const [isDepartmentEmpty, setIsDepartmentEmpty] = useState(false);
  // ✅ ADDED: Item Type is mandatory and always visible (not hidden with extra fields)
  // ✅ CHANGED: itemType now stores the selected option object ({ id, label, name }) fetched from the API
  const [itemType, setItemType] = useState<any>(null);
  const [isItemTypeEmpty, setIsItemTypeEmpty] = useState(false);
  const [openItemType, setOpenItemType] = useState(false);

  // ✅ ADDED: Item Type list fetched from API, loading flag
  const [itemTypeOptions, setItemTypeOptions] = useState<any[]>([]);
  const [isLoadingItemTypes, setIsLoadingItemTypes] = useState(false);
  const [isLoadingButton, setIsLoadingButton] = useState(false);
  const [open, setOpen] = useState(false);
  const [isOpenDepartmentAddModal, setIsOpenDepartmentAddModal] =
    useState(false);

  const [weekFrom, setWeekFrom] = useState<number>(1);
  const [weekTo, setWeekTo] = useState<number>(7);
  const [timeFrom, setTimeFrom] = useState<number>(0);
  const [timeTo, setTimeTo] = useState<number>(23);
  const [selectedTags, setSelectedTags] = useState<any[]>([]);
  const [description, setDescription] = useState("");
  const [colour, setColour] = useState("");
  const [nextViewOrder, setNextViewOrder] = useState<number>(0);

  // ✅ ADDED: controls visibility of the "extra" (non-essential) fields
  const [showExtraFields, setShowExtraFields] = useState(false);

  // ✅ ADDED: Step 2 modal - Price & Size Type (opens right after "Next" is clicked)
  const [isPriceSizeModalOpen, setIsPriceSizeModalOpen] = useState(false);
  // ✅ ADDED: true while the create-product API call (triggered by "Next") is in
  // flight - the step-2 modal shows a loading spinner during this time
  const [isCreatingProduct, setIsCreatingProduct] = useState(false);
  // ✅ ADDED: holds the just-created wrapper (id/version), the Redux payload used to
  // add it, and its category id - needed to patch price/sizeType once Save is clicked
  const [createdProductContext, setCreatedProductContext] = useState<any>(null);
  const [price, setPrice] = useState("");
  const [sizeType, setSizeType] = useState("");
  // ✅ ADDED: In Tax / Out Tax - entered on the step-2 modal along with Price/Size
  const [inTax, setInTax] = useState("");
  const [outTax, setOutTax] = useState("");
  // ✅ ADDED: PLU / Barcode / UPC Barcode / Print Name - hidden by default on the
  // step-2 modal, revealed via a "Show Extra Fields" toggle (same pattern as step-1)
  const [plu, setPlu] = useState("");
  const [barcode, setBarcode] = useState("");
  const [upcBarcode, setUpcBarcode] = useState("");
  const [printName, setPrintName] = useState("");
  const [showPriceExtraFields, setShowPriceExtraFields] = useState(false);
  // ✅ CHANGED: priceError now holds the specific validation message
  // ("Price is required" or "Price must be greater than 0")
  const [priceError, setPriceError] = useState("");

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<any>({
    defaultValues: {
      name: "",
      plu: "",
      barcode: "",
      upcBarcode: "",
      nameShort: "",
    },
  });

  const getCategoryIdValue = (catId: any): number => {
    if (!catId) return 0;
    if (typeof catId === "number") return catId;
    if (typeof catId === "object" && catId.id) return catId.id;
    return 0;
  };

  const normalizeResponseData = (data: any): any => {
    const normalized = { ...data };
    if (normalized.categoryId && typeof normalized.categoryId === "object") {
      normalized.categoryId = normalized.categoryId.id;
    }
    if (normalized.itemWrapperId) {
      if (
        normalized.itemWrapperId.categoryId &&
        typeof normalized.itemWrapperId.categoryId === "object"
      ) {
        normalized.itemWrapperId.categoryId =
          normalized.itemWrapperId.categoryId.id;
      }
    }
    return normalized;
  };

  // ✅ FIXED: Enhanced calculateNextViewOrder function
  const calculateNextViewOrder = useCallback(() => {
    // 1. First try from selectedProduct prop
    if (selectedProduct && selectedProduct.length > 0) {
      const viewOrders = selectedProduct
        .map((p: any) => p.viewOrder || 0)
        .filter(
          (order: number) =>
            order !== undefined && order !== null && order >= 0,
        );

      if (viewOrders.length > 0) {
        const maxViewOrder = Math.max(...viewOrders);
        return maxViewOrder + 1;
      }
    }

    // 2. If selectedProduct not available, try from menuNodeList
    if (menuNodeList && categoryId) {
      const actualCategoryId = getCategoryIdValue(categoryId);
      const category =
        menuNodeList[selectedMenuId]?.category?.[actualCategoryId];
      if (category?.wrapper) {
        const wrappers = Object.values(category.wrapper);
        const viewOrders = wrappers
          .map((w: any) => w.viewOrder || 0)
          .filter(
            (order: number) =>
              order !== undefined && order !== null && order >= 0,
          );

        if (viewOrders.length > 0) {
          return Math.max(...viewOrders) + 1;
        }
      }
    }

    // 3. Fallback to 1
    return 1;
  }, [selectedProduct, menuNodeList, selectedMenuId, categoryId]);

  useEffect(() => {
    if (isOpen) {
      reset({ name: "", plu: "", barcode: "", upcBarcode: "", nameShort: "" });
      setSelectedDepartmentObj({});
      setIsDepartmentEmpty(false);
      setItemType(null); // ✅ ADDED: reset Item Type
      setIsItemTypeEmpty(false); // ✅ ADDED: reset Item Type validation
      setWeekFrom(1);
      setWeekTo(7);
      setTimeFrom(0);
      setTimeTo(23);
      setSelectedTags([]);
      setDescription("");
      setColour("");
      setShowExtraFields(false); // ✅ ADDED: reset extra-fields visibility on open
      // ✅ ADDED: reset step-2 (Price / Size Type) modal state whenever step-1 opens fresh
      setIsPriceSizeModalOpen(false);
      setIsCreatingProduct(false);
      setCreatedProductContext(null);
      setPrice("");
      setSizeType("");
      setInTax("");
      setOutTax("");
      setPlu(""); // ✅ ADDED
      setBarcode(""); // ✅ ADDED
      setUpcBarcode(""); // ✅ ADDED
      setPrintName(""); // ✅ ADDED
      setShowPriceExtraFields(false); // ✅ ADDED
      setPriceError("");
      // ✅ Use enhanced calculateNextViewOrder
      const nextOrder = calculateNextViewOrder();
      setNextViewOrder(nextOrder);
    }
  }, [isOpen, reset, calculateNextViewOrder]);

  const handleDepartmentChange = (e: any, departmentData: any) => {
    setIsDepartmentEmpty(false);
    setSelectedDepartmentObj(departmentData);
  };

  // ✅ NOTE: handleItemTypeChange is defined further below, next to fetchItemTypes.

  const handleOpenDepartmentAddModal = () => {
    setIsOpenDepartmentAddModal(true);
  };

  const handleCloseDepartmentAddModal = () => {
    setIsOpenDepartmentAddModal(false);
  };

  const handleTagsChange = (e: any, newValue: any[]) => {
    setSelectedTags(newValue);
  };

  const getDayName = (dayNumber: number): string => {
    const days = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ];
    return days[dayNumber - 1] || "Monday";
  };

  const getDayNumber = (dayName: string): number => {
    const days = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ];
    return days.indexOf(dayName) + 1 || 1;
  };

  const getTimeString = (hourNumber: number): string => {
    if (hourNumber === 0) return "12:00 AM";
    if (hourNumber < 12)
      return `${hourNumber.toString().padStart(2, "0")}:00 AM`;
    if (hourNumber === 12) return "12:00 PM";
    return `${(hourNumber - 12).toString().padStart(2, "0")}:00 PM`;
  };

  const getTimeNumber = (timeString: string): number => {
    const match = timeString.match(/(\d+):00\s*(AM|PM)/);
    if (!match) return 0;
    let hour = parseInt(match[1]);
    const ampm = match[2];
    if (ampm === "PM" && hour !== 12) hour += 12;
    if (ampm === "AM" && hour === 12) hour = 0;
    return hour;
  };

  const weekDays = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  // ✅ ADDED: Fetch Item Types from API (same call as OverviewSection.tsx)
  const fetchItemTypes = useCallback(async () => {
    setIsLoadingItemTypes(true);
    try {
      const response = await fetchItemTypesDetails(match.params.locationId);
      if (response?.data?.data) {
        const formattedOptions = response.data.data.map((item: any) => ({
          id: item.id,
          label: item.type,
          name: item.type,
        }));
        setItemTypeOptions(formattedOptions);
      }
    } catch (error) {
      console.error("Error fetching item types:", error);
    } finally {
      setIsLoadingItemTypes(false);
    }
  }, [match.params.locationId]);

  // ✅ ADDED: Load Item Types once when the modal component mounts
  useEffect(() => {
    fetchItemTypes();
  }, [fetchItemTypes]);

  // ✅ ADDED: Handle selecting an Item Type
  const handleItemTypeChange = (event: any, value: any) => {
    setIsItemTypeEmpty(false);
    setItemType(value);
  };

  const timeOptions = [
    "12:00 AM",
    "01:00 AM",
    "02:00 AM",
    "03:00 AM",
    "04:00 AM",
    "05:00 AM",
    "06:00 AM",
    "07:00 AM",
    "08:00 AM",
    "09:00 AM",
    "10:00 AM",
    "11:00 AM",
    "12:00 PM",
    "01:00 PM",
    "02:00 PM",
    "03:00 PM",
    "04:00 PM",
    "05:00 PM",
    "06:00 PM",
    "07:00 PM",
    "08:00 PM",
    "09:00 PM",
    "10:00 PM",
    "11:00 PM",
  ];

  const [localWeekFrom, setLocalWeekFrom] = useState<string>("Monday");
  const [localWeekTo, setLocalWeekTo] = useState<string>("Sunday");
  const [localTimeFrom, setLocalTimeFrom] = useState<string>("12:00 AM");
  const [localTimeTo, setLocalTimeTo] = useState<string>("11:00 PM");

  useEffect(() => {
    if (weekFrom) {
      setLocalWeekFrom(getDayName(weekFrom));
    }
  }, [weekFrom]);

  useEffect(() => {
    if (weekTo) {
      setLocalWeekTo(getDayName(weekTo));
    }
  }, [weekTo]);

  useEffect(() => {
    if (timeFrom !== undefined) {
      setLocalTimeFrom(getTimeString(timeFrom));
    }
  }, [timeFrom]);

  useEffect(() => {
    if (timeTo !== undefined) {
      setLocalTimeTo(getTimeString(timeTo));
    }
  }, [timeTo]);

  const handleWeekFromChange = (event: any, newValue: string | null) => {
    if (!newValue) return;
    setLocalWeekFrom(newValue);
    const dayNumber = getDayNumber(newValue);
    setWeekFrom(dayNumber);
  };

  const handleWeekToChange = (event: any, newValue: string | null) => {
    if (!newValue) return;
    setLocalWeekTo(newValue);
    const dayNumber = getDayNumber(newValue);
    setWeekTo(dayNumber);
  };

  const handleTimeFromChange = (event: any, newValue: string | null) => {
    if (!newValue) return;
    setLocalTimeFrom(newValue);
    const hourNumber = getTimeNumber(newValue);
    setTimeFrom(hourNumber);
  };

  const handleTimeToChange = (event: any, newValue: string | null) => {
    if (!newValue) return;
    setLocalTimeTo(newValue);
    const hourNumber = getTimeNumber(newValue);
    setTimeTo(hourNumber);
  };

  // ✅ CHANGED: "Next" button now behaves like the original submit - it calls the
  // create-product API right away. The step-2 (Price/Size Type) modal opens
  // immediately showing a loading spinner while the request is in flight, and
  // switches to the Price/Size Type fields once the response arrives.
  const handleNextStep = async (data: any) => {
    if (!selectedDepartmentObj?.id) {
      setIsDepartmentEmpty(true);
      return;
    }

    if (!itemType) {
      setIsItemTypeEmpty(true);
      return;
    }

    const { plu, barcode, upcBarcode, nameShort, name } = data;

    // ✅ Close step-1 and open step-2 right away, showing its loading state
    // while the create-product request is in flight.
    setIsOpen(false);
    setIsPriceSizeModalOpen(true);
    setIsCreatingProduct(true);

    const actualCategoryId = getCategoryIdValue(categoryId);

    let body: any = {
      name: name,
      nameShort: nameShort || "",
      locationId: match.params.locationId,
      valid: true,
      plu: plu || "",
      extId: "",
      barcode: barcode || "",
      upcBarcode: upcBarcode || "",
      departmentId: selectedDepartmentObj.id,
      categoryId: actualCategoryId,
      itemType: itemType?.id, // ✅ send the Item Type id (API expects the id, not the option object)
      weekFrom: weekFrom,
      weekTo: weekTo,
      timeFrom: timeFrom,
      timeTo: timeTo,
      desc: description || "",
      colour: colour || "",
      isAvailableForEatPresto: false,
      isAvailableForDeliverect: false,
      viewOrder: nextViewOrder,
    };

    if (selectedTags.length > 0) {
      body.tags = selectedTags.map((tag: any) => tag.id).join(",");
    }

    if (!isRoot) {
      body.directoryId = directoryId;
      body.directoryLevel = directoryLevel;
    }

    try {
      const summaryString = `${name} Product wrapper is added`;

      const res = await createProductWrapper(
        match.params.locationId,
        body,
        "Menus Changed",
        summaryString,
        "menu",
      );

      const cloneMenuNodeList = _.cloneDeep(menuNodeList);

      let newProduct = normalizeResponseData(res.data.data);
      const newWrapper = newProduct;

      // ✅ Use the viewOrder from response or fallback to calculated value
      const finalViewOrder = newWrapper.viewOrder || nextViewOrder;

      const newItemForRedux = {
        id: newWrapper.id,
        valid: newWrapper.valid !== undefined ? newWrapper.valid : true,
        version: newWrapper.version || 0,
        name: newWrapper.name,
        colour: newWrapper.colour || colour || "",
        img: newWrapper.img || "",
        desc: newWrapper.desc || description || "",
        directory:
          newWrapper.directory === true || newWrapper.directory === "true",
        directoryId: newWrapper.directoryId || -99,
        directoryLevel: newWrapper.directoryLevel || 1,
        itemType: newWrapper.itemType || itemType?.id || "starters",
        allYouCanEat: newWrapper.allYouCanEat || false,
        kiosk: newWrapper.kiosk || 1,
        cover: newWrapper.cover || "",
        nameShort: newWrapper.nameShort || "",
        viewOrder: finalViewOrder, // ✅ Use finalViewOrder
        weekFrom: newWrapper.weekFrom || weekFrom,
        weekTo: newWrapper.weekTo || weekTo,
        timeFrom: newWrapper.timeFrom || timeFrom,
        timeTo: newWrapper.timeTo || timeTo,
        tags:
          newWrapper.tags ||
          (selectedTags.length > 0
            ? selectedTags.map((tag: any) => tag.id).join(",")
            : ""),
      };

      // ✅ Update Redux store
      dispatch(updateDirectoryInStore(newItemForRedux));

      // ✅ Then update local state with correct viewOrder
      if (isRoot) {
        if (
          !cloneMenuNodeList[selectedMenuId]?.category[actualCategoryId]
            ?.wrapper
        ) {
          if (!cloneMenuNodeList[selectedMenuId])
            cloneMenuNodeList[selectedMenuId] = {};
          if (!cloneMenuNodeList[selectedMenuId].category)
            cloneMenuNodeList[selectedMenuId].category = {};
          if (!cloneMenuNodeList[selectedMenuId].category[actualCategoryId])
            cloneMenuNodeList[selectedMenuId].category[actualCategoryId] = {};
          if (
            !cloneMenuNodeList[selectedMenuId].category[actualCategoryId]
              .wrapper
          ) {
            cloneMenuNodeList[selectedMenuId].category[
              actualCategoryId
            ].wrapper = {};
          }
        }
        cloneMenuNodeList[selectedMenuId].category[actualCategoryId].wrapper[
          newWrapper.id
        ] = {
          ...newWrapper,
          viewOrder: finalViewOrder, // ✅ Use finalViewOrder
        };
      } else {
        const updateProductName = (node: any): boolean => {
          if (node && node.id === wrapperId) {
            if (!node?.wrapper) {
              node.wrapper = {};
            }
            node.wrapper[newWrapper.id] = {
              ...newWrapper,
              viewOrder: finalViewOrder, // ✅ Use finalViewOrder
            };
            return true;
          }

          if (node && node.wrapper) {
            for (const key in node.wrapper) {
              if (Object.hasOwnProperty.call(node.wrapper, key)) {
                const child = node.wrapper[key];
                if (updateProductName(child)) {
                  return true;
                }
              }
            }
          }
          return false;
        };

        if (
          cloneMenuNodeList[menuId] &&
          cloneMenuNodeList[menuId].category[actualCategoryId]
        ) {
          const parentWrapper =
            cloneMenuNodeList[menuId].category[actualCategoryId].wrapper;
          if (parentWrapper && parentWrapper[firstWrapperId]) {
            updateProductName(parentWrapper[firstWrapperId]);
          }
        }
      }

      // ✅ Update local state
      setMenuNodeList(cloneMenuNodeList);

      // ✅ Product created - stop the loading spinner and reveal the Price/Size
      // Type fields. Keep enough context (wrapper id/version, category id, and
      // the Redux payload) so Save can patch price/sizeType afterwards.
      setCreatedProductContext({
        wrapper: newWrapper,
        reduxItem: newItemForRedux,
        actualCategoryId,
      });
      setIsCreatingProduct(false);
    } catch (err) {
      console.error("ProductAddModal - Error:", err);
      setIsCreatingProduct(false);
      setIsPriceSizeModalOpen(false);
      setIsOpen(true); // ✅ reopen step-1 so the user can review and retry
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
    }
  };

  // ✅ ADDED: handlers for the new Price / Size Type fields
  const handlePriceChange = (e: any) => {
    setPriceError("");
    setPrice(e.target.value);
  };

  const handleSizeTypeChange = (e: any) => {
    setSizeType(e.target.value);
  };

  // ✅ ADDED: handlers for In Tax / Out Tax
  const handleInTaxChange = (e: any) => {
    setInTax(e.target.value);
  };

  const handleOutTaxChange = (e: any) => {
    setOutTax(e.target.value);
  };

  // ✅ ADDED: handlers for the hidden PLU / Barcode / UPC Barcode / Print Name fields
  const handlePluChange = (e: any) => {
    setPlu(e.target.value);
  };

  const handleBarcodeChange = (e: any) => {
    setBarcode(e.target.value);
  };

  const handleUpcBarcodeChange = (e: any) => {
    setUpcBarcode(e.target.value);
  };

  const handlePrintNameChange = (e: any) => {
    setPrintName(e.target.value);
  };

  // ✅ CHANGED: "Save" on the step-2 modal now patches price/sizeType onto the
  // product that was already created when "Next" was clicked.
  const handleSavePriceSize = async () => {
    // ✅ Price is mandatory and must be greater than 0
    const priceValue = parseFloat(price);
    if (!price || !price.toString().trim()) {
      setPriceError("Price is required");
      return;
    }
    if (isNaN(priceValue) || priceValue <= 0) {
      setPriceError("Price must be greater than 0");
      return;
    }

    if (!createdProductContext?.wrapper) {
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      return;
    }

    const { wrapper, reduxItem, actualCategoryId } = createdProductContext;

    setIsLoadingButton(true);

    const body = {
      locationId: match.params.locationId,
      price: price,
      sizeType: sizeType || "",
      inTax: inTax || "",
      outTax: outTax || "",
      // ✅ CHANGED: these are hidden by default (behind "Show Extra Fields") but
      // their entered values (if any) are now sent instead of hardcoded blanks
      plu: plu || "",
      barcode: barcode || "",
      upcBarcode: upcBarcode || "",
      printName: printName || "",
      id: wrapper.id,
      version: wrapper.version,
    };

    try {
      const res = await updateWrapperInfo(
        match.params.locationId,
        body,
        "Menus Changed",
        `Set price for ${wrapper.name}`,
        "menu",
      );

      const updatedItem = res?.data?.data || {
        ...wrapper,
        price,
        sizeType,
        inTax,
        outTax,
        plu,
        barcode,
        upcBarcode,
        printName,
      };

      // ✅ Patch price/sizeType onto the item already inserted into
      // menuNodeList when the product was created.
      const cloneMenuNodeList = _.cloneDeep(menuNodeList);

      const findAndUpdateWrapperById = (
        node: any,
        targetId: number,
        updatedData: any,
      ): boolean => {
        if (!node) return false;
        if (node.id === targetId) {
          Object.assign(node, updatedData);
          return true;
        }
        if (node.wrapper && typeof node.wrapper === "object") {
          for (const key in node.wrapper) {
            if (
              node.wrapper[key] &&
              findAndUpdateWrapperById(node.wrapper[key], targetId, updatedData)
            ) {
              return true;
            }
          }
        }
        return false;
      };

      const categoryWrapper =
        cloneMenuNodeList[selectedMenuId]?.category?.[actualCategoryId]
          ?.wrapper;
      if (categoryWrapper) {
        if (categoryWrapper[wrapper.id]) {
          categoryWrapper[wrapper.id] = {
            ...categoryWrapper[wrapper.id],
            ...updatedItem,
          };
        } else {
          for (const key in categoryWrapper) {
            if (
              findAndUpdateWrapperById(categoryWrapper[key], wrapper.id, updatedItem)
            ) {
              break;
            }
          }
        }
      }
      setMenuNodeList(cloneMenuNodeList);

      // ✅ Update Redux store with the merged item (original creation payload + price/sizeType/inTax/outTax/plu/barcode/upcBarcode/printName)
      const finalReduxItem = {
        ...reduxItem,
        price: updatedItem.price ?? price,
        sizeType: updatedItem.sizeType ?? sizeType,
        inTax: updatedItem.inTax ?? inTax,
        outTax: updatedItem.outTax ?? outTax,
        plu: updatedItem.plu ?? plu, // ✅ ADDED
        barcode: updatedItem.barcode ?? barcode, // ✅ ADDED
        upcBarcode: updatedItem.upcBarcode ?? upcBarcode, // ✅ ADDED
        printName: updatedItem.printName ?? printName, // ✅ ADDED
        version: updatedItem.version ?? wrapper.version,
      };
      dispatch(updateDirectoryInStore(finalReduxItem));

      setIsLoadingButton(false);
      setIsPriceSizeModalOpen(false);
      setIsOpenDirectoryOrProductSelectionModal(false);
      setSuccess(SUCCESSFULLY_CREATED);

      if (onItemAdded) {
        onItemAdded({ ...wrapper, ...updatedItem });
      }
    } catch (err) {
      console.error("ProductAddModal - Error updating price/size:", err);
      setIsLoadingButton(false);
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
    }
  };

  const classes = useStyles();

  return (
    <>
      <DialogCommonDefault
        open={isOpen}
        setOpen={setIsOpen}
        isNeedFixedHeight={false}
        maxWidth="md"
      >
        <DialogTitle>Add New Product</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography style={{ paddingLeft: "4px" }}>
                Name <span style={{ color: "#f44336" }}>*</span>
              </Typography>
              <TextfieldDefaultNew
                id="name"
                name="name"
                type="text"
                inputRef={register({
                  required: "Name is required",
                })}
                placeholder="Enter product name"
              />
              {errors.name ? (
                <ValidationMessage message={errors.name?.message} />
              ) : (
                ""
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography>
                  Select Department <span style={{ color: "#f44336" }}>*</span>
                </Typography>
                <Button
                  style={{ textTransform: "none" }}
                  color="secondary"
                  autoFocus
                  onClick={handleOpenDepartmentAddModal}
                >
                  {"Add new department"}
                </Button>
              </div>
              <Autocomplete
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                size={"small"}
                id="locationSelectGlobal"
                color="inherit"
                options={stockDepartmentSelectedNode || []}
                value={selectedDepartmentObj}
                getOptionLabel={(option: any) => option?.name || ""}
                disableClearable
                onChange={handleDepartmentChange}
                classes={{ root: classes.autoComplete }}
                popupIcon={<ArrowDropDownIcon color={"inherit"} />}
                renderOption={(props: any) => {
                  return (
                    <Grid
                      container
                      {...props}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        zIndex: 0,
                        height: "20px",
                        marginBottom: "4px",
                      }}
                    >
                      <Grid item xs={12}>
                        <Typography
                          variant="body2"
                          className={classes.titleColor}
                        >
                          {props?.name}
                        </Typography>
                      </Grid>
                    </Grid>
                  );
                }}
                renderInput={(params: any) => (
                  <TextField
                    color="inherit"
                    {...params}
                    label=""
                    placeholder="Select Department"
                    variant="outlined"
                  />
                )}
              />
              {isDepartmentEmpty ? (
                <ValidationMessage message={"Department is required"} />
              ) : (
                ""
              )}
            </Grid>

            {/* ✅ ADDED: Item Type - mandatory, always visible (not part of hidden extra fields).
                Options are fetched from the API (fetchItemTypesDetails), same as OverviewSection.tsx. */}
            <Grid item xs={12} sm={6}>
              <Typography style={{ paddingLeft: "4px" }}>
                Item Type <span style={{ color: "#f44336" }}>*</span>
              </Typography>
              <Autocomplete
                open={openItemType}
                onOpen={() => setOpenItemType(true)}
                onClose={() => setOpenItemType(false)}
                size="small"
                options={itemTypeOptions}
                loading={isLoadingItemTypes}
                value={itemType}
                getOptionLabel={(option: any) =>
                  option?.label || option?.name || ""
                }
                disableClearable
                onChange={handleItemTypeChange}
                classes={{ root: classes.autoComplete }}
                popupIcon={<ArrowDropDownIcon color={"inherit"} />}
                renderInput={(params: any) => (
                  <TextField
                    color="inherit"
                    {...params}
                    label=""
                    placeholder="Select Item Type"
                    variant="outlined"
                  />
                )}
              />
              {isItemTypeEmpty ? (
                <ValidationMessage message={"Item Type is required"} />
              ) : (
                ""
              )}
            </Grid>

            {/* ✅ MOVED UP + FULL WIDTH: Description is now shown by default, full width row */}
            <Grid item xs={12}>
              <Typography style={{ paddingLeft: "4px" }}>
                Description
              </Typography>
              <TextfieldDefaultNew
                id="description"
                name="description"
                type="text"
                multiline
                rows={3}
                value={description}
                onChange={(e: any) => setDescription(e.target.value)}
                placeholder="Enter description"
                inputRef={register({
                  required: false,
                })}
              />
            </Grid>

            {/* ✅ MOVED BELOW DESCRIPTION: Colour is now shown by default, below Description */}
            <Grid item xs={12} sm={6}>
              <Typography style={{ paddingLeft: "4px" }}>Colour</Typography>
              <ColorSelection
                title={"Color"}
                colorCode={colour}
                setColor={(colorVal: any) => setColour(colorVal.hex)}
                setInitialData={() => setColour("")}
              />
            </Grid>

            {/* ✅ ADDED: Toggle button to show/hide the extra fields below */}
            <Grid item xs={12}>
              <Button
                onClick={() => setShowExtraFields((prev) => !prev)}
                style={{ textTransform: "none" }}
                color="secondary"
              >
                {showExtraFields ? "Hide Extra Fields" : "Show Extra Fields"}
              </Button>
            </Grid>

            {/* ✅ WRAPPED: These fields now only render when showExtraFields is true */}
            {showExtraFields && (
              <>
                <Grid item xs={12} sm={6}>
                  <Typography style={{ paddingLeft: "4px" }}>
                    Week From
                  </Typography>
                  <Autocomplete
                    size="small"
                    options={weekDays}
                    value={localWeekFrom}
                    onChange={handleWeekFromChange}
                    disableClearable
                    classes={{ root: classes.autoComplete }}
                    renderInput={(params: any) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Select day"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography style={{ paddingLeft: "4px" }}>
                    Week To
                  </Typography>
                  <Autocomplete
                    size="small"
                    options={weekDays}
                    value={localWeekTo}
                    onChange={handleWeekToChange}
                    disableClearable
                    classes={{ root: classes.autoComplete }}
                    renderInput={(params: any) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Select day"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography style={{ paddingLeft: "4px" }}>
                    Time From
                  </Typography>
                  <Autocomplete
                    size="small"
                    options={timeOptions}
                    value={localTimeFrom}
                    onChange={handleTimeFromChange}
                    disableClearable
                    classes={{ root: classes.autoComplete }}
                    renderInput={(params: any) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Select time"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography style={{ paddingLeft: "4px" }}>
                    Time To
                  </Typography>
                  <Autocomplete
                    size="small"
                    options={timeOptions}
                    value={localTimeTo}
                    onChange={handleTimeToChange}
                    disableClearable
                    classes={{ root: classes.autoComplete }}
                    renderInput={(params: any) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Select time"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography style={{ paddingLeft: "4px" }}>Tags</Typography>
                  <Autocomplete
                    multiple
                    size="small"
                    options={availableTags || []}
                    getOptionLabel={(option: any) => option?.name || ""}
                    value={selectedTags}
                    onChange={handleTagsChange}
                    classes={{ root: classes.autoComplete }}
                    renderInput={(params: any) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Select tags"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography style={{ paddingLeft: "4px" }}>
                    Name Short
                  </Typography>
                  <TextfieldDefaultNew
                    id="nameShort"
                    name="nameShort"
                    type="text"
                    inputRef={register({
                      required: false,
                    })}
                    placeholder="Enter short name"
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions style={{ marginBottom: "8px", padding: "16px 24px" }}>
          <ButtonCommon
            variant="contained"
            style={{ fontSize: 11, width: "100px", marginRight: 8 }}
            color="yellow"
            onClick={handleCloseProductAddModal}
          >
            Cancel
          </ButtonCommon>
          <ButtonCommon
            variant="contained"
            style={{
              fontSize: 11,
              marginLeft: 4,
              width: "100px",
            }}
            color={"green"}
            onClick={handleSubmit(handleNextStep)}
          >
            {"Next"}
          </ButtonCommon>
        </DialogActions>
      </DialogCommonDefault>

      {/* ✅ ADDED: Step 2 - Price / Size Type modal. Opens as soon as "Next" is
          clicked and the create-product API call starts; shows a loading
          spinner while that call is in flight, then reveals the Price/Size
          Type fields once the response arrives. Save-only (no Cancel button);
          backdrop click / Escape are disabled so the user must complete this
          step via Save. */}
      <Dialog
        open={isPriceSizeModalOpen}
        onClose={() => {}}
        disableBackdropClick
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Product Price & Size</DialogTitle>
        <DialogContent>
          {isCreatingProduct ? (
            // ✅ Loading state while the create-product API call is in flight
            <Grid
              container
              justify="center"
              alignItems="center"
              direction="column"
              style={{ padding: "32px 0" }}
            >
              <CircularProgress size={32} />
              <Typography style={{ marginTop: "16px" }}>
                Creating product...
              </Typography>
            </Grid>
          ) : (
            <Grid container spacing={2} style={{ marginTop: "4px" }}>
              <Grid item xs={12} sm={6}>
                <Typography style={{ paddingLeft: "4px" }}>
                  Price <span style={{ color: "#f44336" }}>*</span>
                </Typography>
                <TextfieldDefaultNew
                  id="price"
                  name="price"
                  type="number"
                  value={price}
                  onChange={handlePriceChange}
                  placeholder="Enter price"
                  inputProps={{ min: 0, step: "0.01" }}
                />
                {priceError ? (
                  <ValidationMessage message={priceError} />
                ) : (
                  ""
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography style={{ paddingLeft: "4px" }}>
                  Size Type
                </Typography>
                <TextfieldDefaultNew
                  id="sizeType"
                  name="sizeType"
                  type="text"
                  value={sizeType}
                  onChange={handleSizeTypeChange}
                  placeholder="Enter size type"
                />
              </Grid>

              {/* ✅ ADDED: In Tax / Out Tax - optional, entered alongside Price/Size. */}
              <Grid item xs={12} sm={6}>
                <Typography style={{ paddingLeft: "4px" }}>In Tax</Typography>
                <TextfieldDefaultNew
                  id="inTax"
                  name="inTax"
                  type="number"
                  value={inTax}
                  onChange={handleInTaxChange}
                  placeholder="Enter in tax"
                  inputProps={{ min: 0, step: "0.01" }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography style={{ paddingLeft: "4px" }}>
                  Out Tax
                </Typography>
                <TextfieldDefaultNew
                  id="outTax"
                  name="outTax"
                  type="number"
                  value={outTax}
                  onChange={handleOutTaxChange}
                  placeholder="Enter out tax"
                  inputProps={{ min: 0, step: "0.01" }}
                />
              </Grid>

              {/* ✅ ADDED: PLU / Barcode / UPC Barcode / Print Name - hidden by default,
                  revealed via this toggle (same pattern as step-1's "Show Extra Fields") */}
              <Grid item xs={12}>
                <Button
                  onClick={() => setShowPriceExtraFields((prev) => !prev)}
                  style={{ textTransform: "none" }}
                  color="secondary"
                >
                  {showPriceExtraFields
                    ? "Hide Extra Fields"
                    : "Show Extra Fields"}
                </Button>
              </Grid>

              {showPriceExtraFields && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Typography style={{ paddingLeft: "4px" }}>PLU</Typography>
                    <TextfieldDefaultNew
                      id="plu"
                      name="plu"
                      type="text"
                      value={plu}
                      onChange={handlePluChange}
                      placeholder="Enter PLU"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography style={{ paddingLeft: "4px" }}>
                      Barcode
                    </Typography>
                    <TextfieldDefaultNew
                      id="barcode"
                      name="barcode"
                      type="text"
                      value={barcode}
                      onChange={handleBarcodeChange}
                      placeholder="Enter barcode"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography style={{ paddingLeft: "4px" }}>
                      UPC Barcode
                    </Typography>
                    <TextfieldDefaultNew
                      id="upcBarcode"
                      name="upcBarcode"
                      type="text"
                      value={upcBarcode}
                      onChange={handleUpcBarcodeChange}
                      placeholder="Enter UPC barcode"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography style={{ paddingLeft: "4px" }}>
                      Print Name
                    </Typography>
                    <TextfieldDefaultNew
                      id="printName"
                      name="printName"
                      type="text"
                      value={printName}
                      onChange={handlePrintNameChange}
                      placeholder="Enter print name"
                    />
                  </Grid>
                </>
              )}
            </Grid>
          )}
        </DialogContent>
        {!isCreatingProduct && (
          <DialogActions
            style={{ marginBottom: "8px", padding: "16px 24px" }}
          >
            <ButtonCommon
              variant="contained"
              style={{ fontSize: 11, width: "100px" }}
              isLoadingPage={isLoadingButton}
              color={"green"}
              onClick={handleSavePriceSize}
            >
              {"Save"}
            </ButtonCommon>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};

export default ProductAddModal;
